import React, { useState, useMemo, useRef, useEffect } from 'react';
import { TextField, TextFieldProps, Tooltip } from '@mui/material';
import { numberToWordsWithCurrency } from '../../utils/NumberToWords';

interface AmountInputProps extends Omit<TextFieldProps, 'type'> {
  value: string | number;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  showWordsTooltip?: boolean;
  currency?: string;
}

/**
 * Reusable Amount Input component with number-to-words tooltip
 * Automatically shows amount in words when user enters a value
 */
const AmountInput: React.FC<AmountInputProps> = ({
  value,
  onChange,
  showWordsTooltip = true,
  currency = 'rupees',
  ...textFieldProps
}) => {
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLDivElement>(null);

  // Convert value to number and generate words
  const amountInWords = useMemo(() => {
    if (!value || value === '' || value === 0) return '';
    
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue) || numValue === 0) return '';
    
    return numberToWordsWithCurrency(numValue, currency);
  }, [value, currency]);

  // Show tooltip when value changes and is valid
  useEffect(() => {
    if (showWordsTooltip && amountInWords) {
      setOpen(true);
      // Auto-hide after 5 seconds if user doesn't interact
      const timer = setTimeout(() => {
        setOpen(false);
      }, 5000);
      return () => clearTimeout(timer);
    } else {
      setOpen(false);
    }
  }, [amountInWords, showWordsTooltip]);

  const handleFocus = () => {
    if (showWordsTooltip && amountInWords) {
      setOpen(true);
    }
  };

  const handleBlur = () => {
    // Keep tooltip open briefly on blur to allow reading
    setTimeout(() => {
      setOpen(false);
    }, 200);
  };

  const handleMouseEnter = () => {
    if (showWordsTooltip && amountInWords) {
      setOpen(true);
    }
  };

  const handleMouseLeave = () => {
    setOpen(false);
  };

  // Determine if we should show the tooltip
  const shouldShowTooltip = Boolean(showWordsTooltip && amountInWords && open);

  return (
    <Tooltip
      title={amountInWords}
      open={shouldShowTooltip}
      arrow
      placement="top"
      enterDelay={300}
      leaveDelay={200}
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'primary.main',
            fontSize: '0.875rem',
            fontWeight: 500,
            maxWidth: 400,
            textTransform: 'capitalize',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          },
        },
        arrow: {
          sx: {
            color: 'primary.main',
          },
        },
      }}
    >
      <TextField
        {...textFieldProps}
        ref={inputRef}
        type="number"
        value={value}
        onChange={onChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        InputProps={{
          ...textFieldProps.InputProps,
        }}
      />
    </Tooltip>
  );
};

export default AmountInput;
